import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Activity, AlertCircle, CheckCircle2, Loader2, Play, Square, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODEL_URL = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
const VISION_WASM = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

// MediaPipe Pose landmark indices
const LM = {
  NOSE: 0,
  LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13, RIGHT_ELBOW: 14,
  LEFT_WRIST: 15, RIGHT_WRIST: 16,
  LEFT_HIP: 23, RIGHT_HIP: 24,
  LEFT_KNEE: 25, RIGHT_KNEE: 26,
  LEFT_ANKLE: 27, RIGHT_ANKLE: 28,
};

// Angle calculator (in degrees) between 3 points a-b-c with b as the vertex
function angleAt(a, b, c) {
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const magAB = Math.hypot(ab.x, ab.y);
  const magCB = Math.hypot(cb.x, cb.y);
  if (magAB === 0 || magCB === 0) return 0;
  const cos = Math.max(-1, Math.min(1, dot / (magAB * magCB)));
  return (Math.acos(cos) * 180) / Math.PI;
}

// Exercise configurations: angles + thresholds that define an up/down state
const EXERCISES = {
  pushup: {
    label: "Push-ups",
    description: "Elbow flexion. Get into push-up position with phone facing your side.",
    minVisibilityKeys: [LM.LEFT_SHOULDER, LM.LEFT_ELBOW, LM.LEFT_WRIST],
    computeAngle: (lms) =>
      angleAt(lms[LM.LEFT_SHOULDER], lms[LM.LEFT_ELBOW], lms[LM.LEFT_WRIST]),
    downThreshold: 90,   // arm bent → less than 90°
    upThreshold: 160,    // arm extended → more than 160°
    targetDescription: "Lower until elbow is bent past 90°, then extend fully.",
  },
  squat: {
    label: "Squats",
    description: "Knee flexion. Stand sideways to the phone, full body in frame.",
    minVisibilityKeys: [LM.LEFT_HIP, LM.LEFT_KNEE, LM.LEFT_ANKLE],
    computeAngle: (lms) =>
      angleAt(lms[LM.LEFT_HIP], lms[LM.LEFT_KNEE], lms[LM.LEFT_ANKLE]),
    downThreshold: 100,  // squat bottom
    upThreshold: 165,    // standing
    targetDescription: "Lower until your thigh is parallel (knee under 100°), then stand fully.",
  },
  situp: {
    label: "Sit-ups",
    description: "Torso flexion. Lie on the floor with phone propped at your side.",
    minVisibilityKeys: [LM.LEFT_SHOULDER, LM.LEFT_HIP, LM.LEFT_KNEE],
    computeAngle: (lms) =>
      angleAt(lms[LM.LEFT_SHOULDER], lms[LM.LEFT_HIP], lms[LM.LEFT_KNEE]),
    downThreshold: 130,  // lying flat (torso roughly aligned with thighs)
    upThreshold: 80,     // sitting up (torso vertical, hip closed)
    targetDescription: "Sit up until torso comes up past 80°, then lower flat.",
    invertCounter: true, // squat/pushup count when going up to upThreshold; situp counts when going up (lower angle)
  },
};

const AIRepCounter = ({ open, onOpenChange, exerciseType = "pushup", onSaved }) => {
  const [step, setStep] = useState("setup"); // setup | counting | result | error
  const [reps, setReps] = useState(0);
  const [position, setPosition] = useState("ready"); // up | down | ready
  const [angle, setAngle] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modelLoading, setModelLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(exerciseType);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef({ inDown: false, reps: 0 }); // tracks rep state machine

  const exercise = EXERCISES[selectedExercise] || EXERCISES.pushup;

  useEffect(() => {
    if (!open) {
      cleanup();
      setStep("setup");
      setReps(0);
      setPosition("ready");
      setAngle(null);
      setErrorMsg("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setSelectedExercise(exerciseType);
  }, [exerciseType]);

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    stateRef.current = { inDown: false, reps: 0 };
  };

  const loadModel = async () => {
    if (landmarkerRef.current) return landmarkerRef.current;
    setModelLoading(true);
    try {
      const filesetResolver = await FilesetResolver.forVisionTasks(VISION_WASM);
      const landmarker = await PoseLandmarker.createFromOptions(filesetResolver, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
      });
      landmarkerRef.current = landmarker;
      return landmarker;
    } catch (e) {
      console.error("MediaPipe load failed:", e);
      throw new Error("Could not load AI model. Check your internet connection.");
    } finally {
      setModelLoading(false);
    }
  };

  const startCounting = async () => {
    // Defensive subscription gate
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/subscription/feature-access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data?.has_advanced_ai) {
        setErrorMsg("AI Rep Counter requires a Pro or Elite subscription. Upgrade to unlock.");
        setStep("error");
        return;
      }
    } catch (e) {
      setErrorMsg("Could not verify subscription. Please try again.");
      setStep("error");
      return;
    }

    setErrorMsg("");
    try {
      await loadModel();
    } catch (e) {
      setErrorMsg(e.message);
      setStep("error");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      console.error("Camera error:", e);
      setErrorMsg("Camera access denied. Allow camera permission and try again.");
      setStep("error");
      return;
    }

    stateRef.current = { inDown: false, reps: 0 };
    setReps(0);
    setStep("counting");

    const tick = (now) => {
      const video = videoRef.current;
      const landmarker = landmarkerRef.current;
      if (!video || !landmarker || video.readyState < 2) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      try {
        const detection = landmarker.detectForVideo(video, now);
        if (detection?.landmarks?.length > 0) {
          const lms = detection.landmarks[0];
          const visible = exercise.minVisibilityKeys.every((k) => lms[k]?.visibility > 0.5);

          if (visible) {
            const a = exercise.computeAngle(lms);
            setAngle(Math.round(a));

            // Rep state machine
            // Standard: going from "up" → "down" → "up" = 1 rep
            // For situp (invertCounter): "down" (flat) → "up" (sitting) = 1 rep
            if (exercise.invertCounter) {
              // For situp: down threshold = lying flat (high angle), up threshold = sitting up (low angle)
              if (a > exercise.downThreshold && !stateRef.current.inDown) {
                stateRef.current.inDown = true;
                setPosition("down");
              } else if (a < exercise.upThreshold && stateRef.current.inDown) {
                stateRef.current.inDown = false;
                stateRef.current.reps += 1;
                setReps(stateRef.current.reps);
                setPosition("up");
              }
            } else {
              // Pushup/squat: high angle = "up", low angle = "down"
              if (a < exercise.downThreshold && !stateRef.current.inDown) {
                stateRef.current.inDown = true;
                setPosition("down");
              } else if (a > exercise.upThreshold && stateRef.current.inDown) {
                stateRef.current.inDown = false;
                stateRef.current.reps += 1;
                setReps(stateRef.current.reps);
                setPosition("up");
              }
            }
          }

          drawOverlay(detection);
        }
      } catch (e) {
        console.error("Pose detect error:", e);
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const drawOverlay = (detection) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!detection?.landmarks?.length) return;
    const lms = detection.landmarks[0];
    const keys = exercise.minVisibilityKeys;

    ctx.fillStyle = position === "down" ? "#3b82f6" : "#f97316";
    ctx.strokeStyle = position === "down" ? "#3b82f6" : "#f97316";
    ctx.lineWidth = 3;

    // Draw the 3 key joints
    keys.forEach((i) => {
      const p = lms[i];
      if (p.visibility > 0.5) {
        ctx.beginPath();
        ctx.arc(p.x * canvas.width, p.y * canvas.height, 8, 0, 2 * Math.PI);
        ctx.fill();
      }
    });

    // Draw the angle lines
    if (keys.length === 3) {
      const a = lms[keys[0]];
      const b = lms[keys[1]];
      const c = lms[keys[2]];
      if (a.visibility > 0.5 && b.visibility > 0.5 && c.visibility > 0.5) {
        ctx.beginPath();
        ctx.moveTo(a.x * canvas.width, a.y * canvas.height);
        ctx.lineTo(b.x * canvas.width, b.y * canvas.height);
        ctx.lineTo(c.x * canvas.width, c.y * canvas.height);
        ctx.stroke();
      }
    }
  };

  const stopAndSave = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStep("result");
  };

  const saveReps = async () => {
    if (reps === 0) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/exercises/log`,
        {
          exercise_type: selectedExercise,
          reps,
          sets: 1,
          notes: "Counted with AI Camera",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Saved! ${reps} ${exercise.label.toLowerCase()}`);
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      console.error("Save failed:", e);
      // Even on save failure, log it locally so user doesn't lose the count
      toast.success(`Recorded: ${reps} ${exercise.label.toLowerCase()} (saved locally)`);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-zinc-950 border border-zinc-800 text-white p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 border-b border-zinc-800">
          <DialogTitle className="text-xl font-heading font-black uppercase tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-500" />
            AI Rep Counter — {exercise.label}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6 pt-4">
          <AnimatePresence mode="wait">
            {/* SETUP */}
            {step === "setup" && (
              <motion.div
                key="setup"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {/* Exercise picker */}
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(EXERCISES).map(([key, ex]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedExercise(key)}
                      className={`p-3 border-2 text-center transition-all ${
                        selectedExercise === key
                          ? "bg-orange-500/10 border-orange-500"
                          : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                      }`}
                      data-testid={`rep-exercise-${key}-btn`}
                    >
                      <p className="font-heading font-black uppercase text-sm">{ex.label}</p>
                    </button>
                  ))}
                </div>

                <p className="text-sm text-zinc-400">{exercise.description}</p>

                <div className="rounded-md bg-orange-500/10 border border-orange-500/30 p-3 text-xs text-orange-200 space-y-1">
                  <p className="font-bold flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> How to set up:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-1 text-orange-100/80">
                    <li>Prop phone 5–10 ft away, side-on (90° from your body)</li>
                    <li>Full body in frame for squats; upper body for push-ups/sit-ups</li>
                    <li>{exercise.targetDescription}</li>
                  </ul>
                </div>

                <Button
                  onClick={startCounting}
                  disabled={modelLoading}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-black uppercase h-12"
                  data-testid="rep-counter-start-btn"
                >
                  {modelLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading AI Model...</>
                  ) : (
                    <><Camera className="w-4 h-4 mr-2" /> Open Camera & Start</>
                  )}
                </Button>
              </motion.div>
            )}

            {/* COUNTING */}
            {step === "counting" && (
              <motion.div
                key="counting"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <div className="relative bg-black aspect-video w-full overflow-hidden rounded-md">
                  <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    className="w-full h-full object-cover"
                  />
                  <canvas
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                  />

                  {/* Rep counter overlay */}
                  <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-md border border-orange-500/30 px-4 py-3 rounded-md">
                    <p className="text-[10px] uppercase tracking-widest text-orange-400 font-mono">{exercise.label}</p>
                    <p className="text-5xl md:text-6xl font-heading font-black text-orange-500 leading-none" data-testid="rep-count-display">
                      {reps}
                    </p>
                  </div>

                  {/* Position indicator */}
                  <div className={`absolute top-4 right-4 px-3 py-1.5 font-heading font-black uppercase text-xs tracking-widest border ${
                    position === "down"
                      ? "bg-blue-500/20 border-blue-500/50 text-blue-300"
                      : position === "up"
                      ? "bg-orange-500/20 border-orange-500/50 text-orange-300"
                      : "bg-zinc-800/50 border-zinc-700 text-zinc-400"
                  }`}>
                    {position === "ready" ? "Get Ready" : position}
                  </div>

                  {/* Angle debug (bottom) */}
                  {angle !== null && (
                    <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-md px-3 py-1.5 text-xs font-mono text-zinc-300">
                      ∠ {angle}°
                    </div>
                  )}
                </div>

                <Button
                  onClick={stopAndSave}
                  className="w-full bg-red-600 hover:bg-red-500 text-white font-heading font-black uppercase tracking-wider text-base h-12"
                  data-testid="rep-counter-stop-btn"
                >
                  <Square className="w-4 h-4 mr-2" /> Stop & Save
                </Button>
              </motion.div>
            )}

            {/* RESULT */}
            {step === "result" && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-5 text-center py-4"
              >
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
                <div>
                  <p className="text-sm text-zinc-500 uppercase tracking-widest mb-2">{exercise.label} Done</p>
                  <p className="text-7xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                    {reps}
                  </p>
                  <p className="text-sm text-zinc-400 mt-2">reps counted by AI</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setStep("setup"); setReps(0); }}
                    variant="outline"
                    className="flex-1 bg-zinc-900 border-zinc-700 text-white hover:bg-zinc-800 font-black uppercase"
                    data-testid="rep-counter-retry-btn"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={saveReps}
                    disabled={saving}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase"
                    data-testid="rep-counter-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Reps"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* ERROR */}
            {step === "error" && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-4 text-center py-4"
              >
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                <p className="text-sm text-zinc-300">{errorMsg}</p>
                <Button
                  onClick={() => { setStep("setup"); setErrorMsg(""); cleanup(); }}
                  className="w-full bg-orange-600 hover:bg-orange-500 text-white font-black uppercase"
                  data-testid="rep-counter-error-back-btn"
                >
                  Back to Setup
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIRepCounter;
